import { FC, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, CssBaseline, Alert, Button, useMediaQuery, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Sidebar } from './Sidebar';
import {
    useMemberships,
    useCompanyContext,
    useCompanyNavigate,
    useCompanyUserStatus,
} from '@lendica/hooks';
import { RebrandAlert } from '@lendica/components';
import { accessToken } from '@lendica/auth';
import { matchHostname } from '@lendica/utils';
import { Navbar } from './components/Navbar';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const SidebarContainer: FC = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
    const [shouldExpand, setShouldExpand] = useState(false);

    const handleToggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const getRebrandDismissKey = () => {
        const token = accessToken?.get?.() ?? '';
        const suffix = token ? token.slice(-12) : 'anon';
        return `rebrand-alert-dismissed-${suffix}`;
    };

    const [showRebrandAlert, setShowRebrandAlert] = useState<boolean>(() => {
        try {
            const key = getRebrandDismissKey();
            return sessionStorage.getItem(key) !== 'true';
        } catch {
            return true;
        }
    });
    const memberships = useMemberships();
    const location = useLocation();
    const companyNavigate = useCompanyNavigate();
    const { activeCompanyId, activeCompanyKey, isCompanyResolved } = useCompanyContext();
    const {
        status: userCompanyStatus,
        loading: statusLoading,
        error: statusError,
    } = useCompanyUserStatus(activeCompanyId);
    const companyStatus = userCompanyStatus?.company?.status;

    const hasRedirectedRef = useRef(false);
    const offerTermsPath = activeCompanyKey ? `/c/${activeCompanyKey}/offerterms` : '';
    const offerTermsRegex = activeCompanyKey
        ? new RegExp(`^/c/${escapeRegExp(activeCompanyKey)}/offerterms(?:/|$)`)
        : null;
    const suppressedAlertRegex = activeCompanyKey
        ? new RegExp(
              `^/c/${escapeRegExp(
                  activeCompanyKey
              )}/(?:offerterms|account|members|file-upload|integrations)(?:/|$)`
          )
        : null;
    const isOnOfferTerms = offerTermsRegex ? offerTermsRegex.test(location.pathname) : false;
    const suppressApprovalAlert = suppressedAlertRegex
        ? suppressedAlertRegex.test(location.pathname)
        : false;
    const shouldSkipOfferTermsRedirect = typeof companyStatus !== 'number' || companyStatus >= 2;
    const requiresOfferTerms = !shouldSkipOfferTermsRedirect;

    const isLoadPending = statusLoading && !hasRedirectedRef.current;

    useEffect(() => {
        hasRedirectedRef.current = false;
    }, [offerTermsPath]);

    useEffect(() => {
        if (hasRedirectedRef.current) return;
        if (!isCompanyResolved) return;
        if (isLoadPending) return;
        if (shouldSkipOfferTermsRedirect) return;
        if (!offerTermsPath) return;

        hasRedirectedRef.current = true;
        if (isOnOfferTerms) return;

        companyNavigate('/offerterms', { replace: true });
    }, [
        isCompanyResolved,
        shouldSkipOfferTermsRedirect,
        offerTermsPath,
        isOnOfferTerms,
        companyNavigate,
        isLoadPending,
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100vh',
                boxSizing: 'border-box',
                py: isMobile ? 0 : 1,
                pr: isMobile ? 0 : 1,
                backgroundColor: 'oklab(0.97 0 0 / 0.5)',
                transition: 'all 0.3s ease-in-out',
            }}
        >
            <CssBaseline />
            {!isMobile && <Sidebar open={sidebarOpen} />}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: isMobile ? 0 : 2,
                    border: isMobile ? 'unset' : '1px solid',
                    borderColor: isMobile ? 'unset' : 'oklab(0.87 0 0 / 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    backgroundColor: 'oklab(0.99 0 0 / 0.5)',
                    transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out',
                    marginLeft: 0,
                }}
            >
                <Navbar sidebarOpen={sidebarOpen} onToggleSidebar={handleToggleSidebar} />
                {showRebrandAlert && (
                    <RebrandAlert
                        sx={{ width: '100%', borderRadius: 0 }}
                        onClose={() => {
                            setShowRebrandAlert(false);
                            try {
                                sessionStorage.setItem(getRebrandDismissKey(), 'true');
                            } catch {}
                        }}
                    />
                )}
                {isCompanyResolved && requiresOfferTerms && !suppressApprovalAlert && (
                    <NoApprovalAlert status={companyStatus} />
                )}
                {isCompanyResolved && (!memberships || memberships.length === 0) && (
                    <Alert
                        severity="error"
                        variant="filled"
                        sx={{ width: '100%', borderRadius: 0 }}
                    >
                        You're not a member of any company. Please contact your administrator to get
                        access.
                    </Alert>
                )}
                <Box
                    component="main"
                    sx={{
                        // borderRadius: 2,
                        // border: '1px solid',
                        // borderColor: 'oklab(0.87 0 0 / 0.5)',
                        overflow: 'auto',
                        display: 'grid',
                        width: '100%',
                        height: '100%',
                        gridTemplateColumns: shouldExpand
                            ? 'minmax(min(480px, 100%), 100%)'
                            : 'min(480px, 100%) 1fr',
                        gridTemplateAreas: '"main ."',
                        transition: 'grid-template-columns 300ms ease',
                    }}
                >
                    {/* Pass setter to children */}
                    <Outlet context={{ setShouldExpand }} />
                </Box>
            </Box>
        </Box>
    );
};

const NoApprovalAlert = ({ status }: { status: number }) => {
    const redirectUri = `https://apply.${matchHostname()}/?utm_source=daylit&utm_medium=portal&utm_campaign=offerterms`;

    const statusMap = new Map<
        number,
        { content: string | React.ReactNode; action: React.ReactNode | null }
    >([
        [
            0,
            {
                content: (
                    <a href={redirectUri} target="_blank" rel="noopener noreferrer">
                        You can use OfferTerms now, but you may need to complete an application to
                        use other funding products. Apply now to get full access.
                    </a>
                ),
                action: (
                    <span>
                        <Button
                            href={redirectUri}
                            color="inherit"
                            size="small"
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<OpenInNewIcon fontSize="inherit" />}
                            sx={{ borderRadius: '4px', fontWeight: 500 }}
                        >
                            Apply now
                        </Button>
                    </span>
                ),
            },
        ],
        [
            1,
            {
                content:
                    "You can use OfferTerms now, and once your application is approved, you'll be able to use other funding products.",
                action: null,
            },
        ],
        [
            3,
            {
                content:
                    'You can use OfferTerms now, but you may need to complete an application to use other funding products. Contact us to learn more.',
                action: (
                    <span>
                        <Button
                            onClick={() => {
                                window?.HubSpotConversations?.widget.open();
                            }}
                            color="inherit"
                            size="small"
                            sx={{ borderRadius: '4px', fontWeight: 500 }}
                        >
                            Contact us
                        </Button>
                    </span>
                ),
            },
        ],
    ]);

    const { content, action } = statusMap.get(status) ?? { content: '', action: null };

    return (
        <Alert
            severity="info"
            variant="filled"
            sx={{
                width: '100%',
                borderRadius: 0,
            }}
            action={action}
        >
            {content}
        </Alert>
    );
};
